import 'pages/BuySubscription/buy-subscription.css';
import { Subscription } from 'pages/Join/Join.types';
import React, { BaseSyntheticEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from 'src/environments/environment';
import useChildren from 'src/hooks';
import useAuth from 'src/hooks/useAuth';
import Page from 'src/layout/Page';
import { TSubscriptionType } from 'src/pages/BuySubscription/BuySubscription.types';
import { DEFAULT_HEADERS } from 'src/shared/constants';
import { useTranslation } from 'react-i18next';
import Calendar from 'react-calendar';
import { Value } from 'src/shared/types';
import { format } from 'date-fns';

const stepTitles = ['', '', ''];

const BuySubscription: React.FC = (): React.ReactElement => {
	const { t, i18n } = useTranslation();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState<Subscription[]>([]);
  const [subscriptionsType, setSubscriptionsType] = useState<TSubscriptionType>('online');
  const [subscribeStep, setSubscribeStep] = useState<number>(0);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<number | null>(null);
  const [storedToken, setStoredToken] = useState<string | undefined>(undefined);
	const [isShowingCalendar, setIsShowingCalendar] = useState<boolean>(false);
	const [calendarValue, setCalendarValue] = useState<Value>(new Date());
	const [calendarLanguage, setCalendarLanguage] = useState(i18n.language);
	const [startDate, setStartDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
	const [promoCode, setPromoCode] = useState<string>('');
  const { children } = useChildren();

  const navigate = useNavigate();
  const { getToken, isAuthenticated } = useAuth();
  const checkAuth = () => {
    if (!isAuthenticated())
			if (i18n.language !== 'en') {
				navigate('/התחברות');
			} else {
				navigate('/en/login');
			}
  };
  useEffect(() => {
    checkAuth();
    const token = getToken();
    if (token) {
      setStoredToken(token);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    if (children?.length === 0) {
      setSubscribeStep(0);
      return;
    }
    if (children?.length > 0) {
      setSubscribeStep(1);
    }
  }, [children]);

  const formRef = useRef() as React.MutableRefObject<HTMLFormElement>;
  useEffect(() => {
    const getData = async () => {
      const data = await fetch(`${API_URL}/subscriptions`, {
        method: 'GET',
      });
      const jsonData = await data.json();
      setSubscriptions(jsonData);
      setFilteredSubscriptions(
        jsonData.filter((subscription: Subscription) => subscription.isOnline),
      );
    };

    getData().then();
  }, []);

	useEffect(() => {
		setCalendarLanguage(i18n.language);
	}, [i18n.language]);

  const registerChild = async () => {
    checkAuth();
    const formData = Object.fromEntries(new FormData(formRef.current));
    const payload = {
      firstname: formData.firstname,
      lastname: formData.lastname,
      age: formData.age,
    };
    const requestString = JSON.stringify(payload);
    const registerChildResponse = await fetch(`${API_URL}/children`, {
      method: 'POST',
      body: requestString,
      headers: {
        ...DEFAULT_HEADERS,
        Authorization: `Bearer ${storedToken}`,
      },
    });
    if (registerChildResponse.status === 200) {
      setSubscribeStep(subscribeStep + 1);
    }
  };

  const buySubscription = async () => {
    checkAuth();
    if (selectedSubscriptionId !== null && startDate !== '') {
      const requestString = JSON.stringify({
        subscriptionId: selectedSubscriptionId,
				startDate,
      });
      const buyResponse = await fetch(`${API_URL}/purchased-subscriptions`, {
        method: 'POST',
        headers: {
          ...DEFAULT_HEADERS,
          Authorization: `Bearer ${storedToken}`,
        },
        body: requestString,
      });
      if (buyResponse.status === 200) {
        const { link } = await buyResponse.json();
        window.location = link;
      }
    }
  };

  const handleStep = (e: any, step: number) => {
    e.preventDefault();
    if (step === 0) {
      registerChild().then();
    }
    if (step === 1) {
      buySubscription().then();
    }
  };

  const handleType = (e: any, sType: TSubscriptionType) => {
    e.preventDefault();
    if (sType !== subscriptionsType) {
      setSubscriptionsType(sType);
      switch (sType) {
        case 'online':
          setFilteredSubscriptions(
            subscriptions.filter((subscription) => subscription.isOnline),
          );
          break;
        case 'offline':
          setFilteredSubscriptions(
            subscriptions.filter((subscription) => !subscription.isOnline),
          );
          break;
        default:
          setFilteredSubscriptions(
            subscriptions.filter((subscription) => subscription.isOnline),
          );
          break;
      }
      setSelectedSubscriptionId(null);
    }
  };

  const handleSelectedSubscription = (e: any, subscriptionId: number) => {
    e.preventDefault();
    setSelectedSubscriptionId(subscriptionId);
  };

	const onChange = (value: any) => {
		setCalendarValue(value);
		setStartDate(format(value as Date, 'yyyy-MM-dd'));
		setIsShowingCalendar(false);
	}

	const handlePromoCode = (e: BaseSyntheticEvent) => {
		setPromoCode(e.target.value);
	}

  return (
    <Page pageClass={'subscribe-page'}>
      {children && (
        <>
          <div className="step-header">
            {stepTitles.map((_step, index) => {
              const isActiveClass = index === subscribeStep ? 'active' : '';
              const key = `tabKey-${index}`;
              return (
                <div className={`step ${isActiveClass}`} key={key}>
                  {t(`pages.buySubscription.stepTitles.${index}`)}
                </div>
              );
            })}
          </div>
          <div className="subscribe-steps">
            <div className="subscribe-step-description">
              <div className="subscribe-step-number">
                {t(`pages.buySubscription.steps.${subscribeStep}.stepNumber`)}
              </div>
              <div className="subscribe-step-title">
                {t(`pages.buySubscription.steps.${subscribeStep}.stepTitle`)}
              </div>
            </div>
            <form className="subscribe-step-form" ref={formRef}>
              <div className="subscribe-form">
                {subscribeStep === 0 && (
                  <div className="personal-data">
                    <div className="input-container">
                      <label htmlFor={'firstname'}>{t('pages.buySubscription.steps.0.firstName')}</label>
                      <input
                        name={'firstname'}
                        id={'firstname'}
                        type="text"
                        required
                      />
                    </div>
                    <div className="input-container">
                      <label htmlFor={'lastname'}>{t('pages.buySubscription.steps.0.lastName')}</label>
                      <input
                        name={'lastname'}
                        id={'lastname'}
                        type="text"
                        required
                      />
                    </div>
                    <div className="input-container">
                      <label htmlFor={'age'}>{t('pages.buySubscription.steps.0.age')}</label>
                      <input name={'age'} id={'age'} type="number" required/>
                    </div>
                    <div className="input-container">
                      <label htmlFor={'parentName'}>{t('pages.buySubscription.steps.0.parentName')}</label>
                      <input
                        name={'parentName'}
                        id={'parentName'}
                        type="text"
                        required
                      />
                    </div>
                    <div className="input-container">
                      <label htmlFor={'phoneNumber'}>{t('pages.buySubscription.steps.0.phoneNumber')}</label>
                      <input
                        name={'phoneNumber'}
                        id={'phoneNumber'}
                        type="text"
                        required
                      />
                    </div>
                    <div className="button-container">
                      <button
                        className={'scroll-to-button'}
                        onClick={(e) => handleStep(e, subscribeStep)}
                      >
                        {t('common.continue')}
                      </button>
                    </div>
                  </div>
                )}
                {subscribeStep === 1 && (
                  <div className="subscription-selection">
                    <div className="button-container start">
                      <button
                        className={`${subscriptionsType === 'online' ? 'active' : ''} scroll-to-button`}
                        onClick={(e) => handleType(e, 'online')}
                      >
                        {t('pages.buySubscription.digital')}
                      </button>
                      <button
                        className={`${subscriptionsType === 'offline' ? 'active' : ''} scroll-to-button`}
                        onClick={(e) => handleType(e, 'offline')}
                      >
                        {t('pages.buySubscription.frontal')}
                      </button>
                    </div>
                    <div className="subscriptions-container">
                      {filteredSubscriptions.map((subscription) => (
                        <div
                          className="subscription-to-select"
                          key={subscription.id}
                        >
                          <div className={'subscription-with-price'}>
                            {subscription.lessonsCount} {t('pages.buySubscription.lessonsPerMonth')} {subscription.price} {t('pages.buySubscription.euros')}
                          </div>
                          <div className="select-subscription-button">
                            <button
                              className={`scroll-to-button select-subscription ${subscription.id === selectedSubscriptionId ? 'active' : ''}`}
                              onClick={(e) => handleSelectedSubscription(e, subscription.id)
                              }
                            >
                              {t('pages.buySubscription.selectSubscription')}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
										<div className="calendar-container">
											<label htmlFor={'startDate'}>
												{t('pages.buySubscription.startDate')}
											</label>
											<div className={'calendar-wrapper'}>
												{isShowingCalendar && <div className="calendar">
													<Calendar
														view={'month'}
														onChange={(value: any) => onChange(value)}
														value={calendarValue}
														calendarType={'hebrew'}
														locale={calendarLanguage}
														minDate={new Date()}
													/>
												</div>}
												<input
													id={'startDate'}
													name={'startDate'}
													type="text"
													readOnly={true}
													onClick={() => setIsShowingCalendar(!isShowingCalendar)}
													value={startDate}
												/>
											</div>
										</div>
										<div className="coupon-code-container">
											<label htmlFor={'startDate'}>
												{t('pages.buySubscription.promoCode')}
											</label>
											<input
												id={'promoCode'}
												name={'promoCode'}
												type="text"
												readOnly={false}
												value={promoCode}
												onChange={handlePromoCode}
											/>
										</div>
                    <div className="button-container next-step">
                      <button
                        className={'scroll-to-button'}
                        onClick={() => setSubscribeStep(0)}
                      >
                        {t('pages.buySubscription.registerAdditionalChild')}
                      </button>
                      <button
                        className={'scroll-to-button'}
                        onClick={(e) => handleStep(e, subscribeStep)}
                      >
                        {t('common.continue')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>
        </>
      )}
    </Page>
  );
};

export default BuySubscription;
